import { useCallback, useEffect, useRef, useState } from 'react';
import { getRegister, saveRegister, getClassOverview, setCeremonyRsvp, assetUrl } from '../../services/api';
import { getRumaalColor } from '../../utils/colors';

// Taking the register on the wall-mounted iPad.
//
// Everything here is sized for someone standing at arm's length from a screen
// they cannot comfortably reach, mid-class, with children waiting. Marks save
// themselves as they are tapped — an instructor who walks away without pressing
// anything must not lose the register.

// Present / Late / Absent keep the colours the admin attendance screen already
// uses, and Late stays blue rather than the yellow in the mockup: instructors
// have been marking Late in blue since the old app, and muscle memory beats a
// swatch.
const MARKS = [
  { id: 'present', label: 'Present', on: 'bg-green-600 text-white', ring: 'ring-green-600' },
  { id: 'late', label: 'Late', on: 'bg-sky-500 text-white', ring: 'ring-sky-500' },
  { id: 'absent', label: 'Absent', on: 'bg-red-500 text-white', ring: 'ring-red-500' },
];

const OFF = 'bg-white text-gray-500 border border-gray-200';

// White rank is #FFFFFF, so on a white card its ring and bar vanish — and White
// is the commonest rank in a beginners class. A hairline outside the colour
// keeps every rank visible without altering any of them.
const RANK_EDGE = 'rgba(0,0,0,.18)';

function Avatar({ student, size = 56 }) {
  const initials = student.name.split(' ').map((w) => w[0]).slice(0, 2).join('');
  return (
    <div
      className="flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100"
      style={{
        width: size, height: size,
        boxShadow: `0 0 0 3px ${getRumaalColor(student.rumaalRank)}, 0 0 0 4px ${RANK_EDGE}`,
      }}
    >
      {student.photoUrl
        ? <img src={assetUrl(student.photoUrl)} alt="" className="h-full w-full object-cover" />
        : <span className="text-lg font-semibold text-gray-400">{initials}</span>}
    </div>
  );
}

export default function ClassRegister({ classId, instructor, onBack }) {
  const [payload, setPayload] = useState(null);
  const [marks, setMarks] = useState({});
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [error, setError] = useState('');
  const [view, setView] = useState('register'); // register | overview
  const [overview, setOverview] = useState(null);
  const [rsvpBusy, setRsvpBusy] = useState(null);

  const marksRef = useRef({});
  const saveTimer = useRef(null);
  const retryTimer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await getRegister(classId);
        if (cancelled) return;
        setPayload(data);
        const initial = {};
        data.students.forEach((s) => { if (s.mark) initial[s.studentKey] = s.mark; });
        setMarks(initial);
        marksRef.current = initial;
      } catch (loadError) {
        if (!cancelled) setError(loadError.response?.data?.error || 'Could not load this class');
      }
    };
    load();
    return () => { cancelled = true; };
  }, [classId]);

  const flush = useCallback(async () => {
    const entries = Object.entries(marksRef.current)
      .filter(([, status]) => status)
      .map(([studentKey, status]) => ({ studentKey, status }));

    if (entries.length === 0) { setSaveState('idle'); return; }

    setSaveState('saving');
    try {
      await saveRegister(classId, { date: payload?.date, entries, instructor });
      setSaveState('saved');
      setError('');
      clearTimeout(retryTimer.current);
    } catch (saveError) {
      setSaveState('error');
      setError(saveError.response?.data?.error || 'Not saved — will keep trying');
      // The academy's wifi is not reliable; keep retrying rather than making a
      // busy instructor notice and press something.
      clearTimeout(retryTimer.current);
      retryTimer.current = setTimeout(flush, 5000);
    }
  }, [classId, instructor, payload?.date]);

  const mark = (studentKey, status) => {
    const next = { ...marksRef.current };
    // Tapping the same mark again clears it — a mis-tap is fixable without a
    // separate undo control.
    if (next[studentKey] === status) delete next[studentKey];
    else next[studentKey] = status;

    marksRef.current = next;
    setMarks(next);
    setSaveState('saving');
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flush, 800);
  };

  const markAllPresent = () => {
    const next = { ...marksRef.current };
    // Only fills the blanks: an instructor who has already marked two absences
    // must not have them overwritten by a convenience button.
    payload.students.forEach((s) => { if (!next[s.studentKey]) next[s.studentKey] = 'present'; });
    marksRef.current = next;
    setMarks(next);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flush, 400);
  };

  useEffect(() => () => {
    clearTimeout(saveTimer.current);
    clearTimeout(retryTimer.current);
  }, []);

  const openOverview = async () => {
    setView('overview');
    if (overview) return;
    try {
      const { data } = await getClassOverview(classId);
      setOverview(data);
    } catch {
      setError('Could not load the class overview');
    }
  };

  const rsvp = async (studentKey, value) => {
    setRsvpBusy(studentKey);
    try {
      await setCeremonyRsvp(studentKey, value, instructor);
      setOverview((current) => ({
        ...current,
        students: current.students.map((s) =>
          s.studentKey === studentKey ? { ...s, rsvp: value } : s),
      }));
    } catch (rsvpError) {
      setError(rsvpError.response?.data?.error || 'Could not save that');
    } finally {
      setRsvpBusy(null);
    }
  };

  if (error && !payload) {
    return (
      <div className="p-10 text-center">
        <p className="text-lg text-red-600">{error}</p>
        <button onClick={onBack} className="mt-4 rounded-xl bg-gray-900 px-6 py-3 text-white">Back</button>
      </div>
    );
  }
  if (!payload) return <div className="p-10 text-center text-lg text-gray-500">Loading…</div>;

  const marked = Object.values(marks).filter(Boolean).length;
  const remaining = payload.students.length - marked;

  const saveLabel = {
    idle: 'Marks save as you tap',
    saving: 'Saving…',
    saved: 'All marks saved',
    error: error || 'Not saved — retrying',
  }[saveState];

  return (
    <div className="flex h-full flex-col bg-[#f7f1e6]">
      <header className="bg-[#221420] px-6 py-4 text-[#f7f1e6]">
        <div className="flex items-center justify-between gap-4">
          {/* The register is the screen the academy looks at all day, so the
              mark lives here rather than only on the way in. */}
          <span className="stable-mark hidden shrink-0 sm:inline-flex">
            <img
              src="/brand/stable-mark-512.png"
              alt="The Stable"
              className="h-11 w-11 rounded-xl object-cover shadow-lg shadow-black/30"
            />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-2xl font-bold">
              {payload.class.day} {payload.class.startTimeLabel} — {payload.class.name}
            </h1>
            <p className="text-sm text-[#f7f1e6]/60">
              {payload.class.timing === 'now' ? 'On now · ' : ''}
              {payload.students.length} students · {instructor}
              {!payload.isToday && ` · ${payload.date}`}
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <button
              onClick={() => (view === 'register' ? openOverview() : setView('register'))}
              className="rounded-xl border border-[#443040] px-5 py-3 text-base font-medium text-[#f7f1e6]/80"
            >
              {view === 'register' ? 'Class overview' : 'Back to register'}
            </button>
            <button
              onClick={onBack}
              className="rounded-xl border border-[#443040] px-5 py-3 text-base font-medium text-[#f7f1e6]/80"
            >
              Switch
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-4">
        {view === 'register' ? (
          <div className="space-y-3">
            {payload.students.map((student) => (
              <div
                key={student.studentKey}
                className="flex items-center gap-4 rounded-2xl border border-[#e2d9c8] bg-white p-3"
              >
                <Avatar student={student} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-xl font-bold text-[#221420]">{student.name}</span>
                    {student.isBirthdayToday && (
                      <span className="shrink-0 rounded-full bg-pink-100 px-3 py-1 text-sm font-semibold text-pink-700">
                        🎂 {student.turningAge ? `${student.turningAge} today` : 'Birthday today'}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    {student.rumaalRank ? `${student.rumaalRank} rank` : 'No rank yet'}
                    {student.status && student.status !== 'Active' && ` · ${student.status}`}
                  </div>
                </div>

                <div className="flex shrink-0 gap-2">
                  {MARKS.map((option) => {
                    const active = marks[student.studentKey] === option.id;
                    return (
                      <button
                        key={option.id}
                        onClick={() => mark(student.studentKey, option.id)}
                        // Big enough to hit accurately at arm's length on a
                        // wall-mounted screen.
                        className={`h-16 w-28 rounded-2xl text-base font-semibold transition-colors ${
                          active ? option.on : OFF
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <ClassOverview
            overview={overview}
            onRsvp={rsvp}
            rsvpBusy={rsvpBusy}
          />
        )}
      </div>

      {view === 'register' && (
        <footer className="flex items-center justify-between gap-4 border-t border-[#e2d9c8] bg-white px-6 py-4">
          <div>
            <div className="text-lg font-semibold text-[#221420]">
              {marked} of {payload.students.length} marked
              {remaining > 0 && <span className="text-[#b03a6e]"> · {remaining} to go</span>}
            </div>
            <div className={`text-sm ${saveState === 'error' ? 'text-red-600' : 'text-gray-500'}`}>
              {saveLabel}
            </div>
          </div>
          <button
            onClick={markAllPresent}
            disabled={remaining === 0}
            className="rounded-2xl bg-[#ee8a1e] px-8 py-4 text-lg font-bold text-[#221420] shadow-[0_3px_0_#c96f08] transition-transform disabled:opacity-40"
          >
            {marked === 0 ? 'Mark all present' : 'Mark the rest present'}
          </button>
        </footer>
      )}
    </div>
  );
}

function ClassOverview({ overview, onRsvp, rsvpBusy }) {
  if (!overview) return <p className="p-6 text-lg text-gray-500">Loading…</p>;

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-[#e2d9c8] bg-white p-4">
        <h2 className="mb-3 text-lg font-bold text-[#221420]">Ranks in this class</h2>
        {overview.rankSpread.length === 0 ? (
          <p className="text-gray-500">Nobody has a rank yet.</p>
        ) : (
          <div className="space-y-2">
            {overview.rankSpread.map((entry) => (
              <div key={entry.rank} className="flex items-center gap-3">
                <span className="w-20 text-sm text-gray-600">{entry.rank}</span>
                <div className="h-5 flex-1 overflow-hidden rounded-md bg-[#efe8da]">
                  <div
                    className="h-full rounded-md"
                    style={{
                      width: `${(entry.count / overview.students.length) * 100}%`,
                      backgroundColor: getRumaalColor(entry.rank),
                      boxShadow: `inset 0 0 0 1px ${RANK_EDGE}`,
                    }}
                  />
                </div>
                <span className="w-8 text-right font-semibold text-[#221420]">{entry.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-[#e2d9c8] bg-white p-4">
        <div className="mb-3 flex items-baseline justify-between">
          <h2 className="text-lg font-bold text-[#221420]">My students</h2>
          {overview.ceremony ? (
            <span className="text-sm text-gray-500">
              {overview.ceremony.name} · {overview.rsvpCount} signed up
            </span>
          ) : (
            <span className="text-sm text-gray-400">No ceremony set up yet</span>
          )}
        </div>

        <div className="space-y-2">
          {overview.students.map((student) => (
            <div key={student.studentKey} className="flex items-center gap-3 rounded-xl border border-[#e2d9c8] p-3">
              <Avatar student={student} size={44} />
              <div className="min-w-0 flex-1">
                <div className="truncate font-semibold text-[#221420]">{student.name}</div>
                <div className="text-sm text-gray-500">
                  {student.rumaalRank || 'No rank'}
                  {student.testingFor && ` → testing ${student.testingFor}`}
                  {student.attendanceRate !== null && ` · ${student.attendanceRate}% attendance`}
                </div>
              </div>

              {overview.ceremony && (
                student.rsvp === 'yes' ? (
                  <button
                    onClick={() => onRsvp(student.studentKey, 'none')}
                    disabled={rsvpBusy === student.studentKey}
                    className="shrink-0 rounded-xl bg-green-600 px-5 py-3 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    ✓ Signed up
                  </button>
                ) : (
                  <button
                    onClick={() => onRsvp(student.studentKey, 'yes')}
                    disabled={rsvpBusy === student.studentKey}
                    className="shrink-0 rounded-xl border border-[#e2d9c8] px-5 py-3 text-sm font-medium text-gray-600 disabled:opacity-50"
                  >
                    Sign up for rank
                  </button>
                )
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
